import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { initJanusForOneVehicle } from "../../utils/onevehicleVideo";
import { initMqttClient } from "../../utils/mqtt-client";
import authApiService from "../../services/auth-api.service";
import operatorApiService from "../../services/operator-api.service";
import {
  recvDataSetCarDrive,
  recvDataSelectCarRet,
  recvDataSetVideoSelect,
  recvDataUpdateScreen,
} from "../../utils/recvData";
import { sendOneVehicleLog } from "./utils/logFormatDate";
import { startAudioMonitor } from "./utils/audioMonitor";
import startLayoutCheck from "./utils/startLayoutCheck";
import { setPlayoutDelay } from "../../utils/setPlayoutDelay";
import GuideCanvas from "./guide/GuideCanvas";
import StatusOverlay from "./status/StatusOverlay";
import { OnevehicleInitData, VehicleInfo } from "../../types/onevehicle";

import "./OneVehicle.scss";
import unplayIcon from "../../assets/imgs/onevehicle/unplay_icon.png";

const OneVehicle = () => {
  const navigate = useNavigate();
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const mqttClientRef = useRef<any>(null);
  const remoteFeedsRef = useRef<Record<string, any>>({});
  const suidsRef = useRef<Record<string, string>>({});
  const audioMonitorRef = useRef<Record<string, { stop: () => void } | null>>(
    {}
  );

  const [initData, setInitData] = useState<OnevehicleInitData | null>(null);
  const [vehicleLabel, setVehicleLabel] = useState("");
  const [vehicleStates, setVehicleStates] = useState<
    { code: string; isVisible: boolean }[]
  >([]);
  const [vehicleList, setVehicleList] = useState<VehicleInfo[]>([]);
  const [baseId, setBaseId] = useState<number | null>(null);
  const [monitorConsoleId, setMonitorConsoleId] = useState<number | null>(null);
  const [onevehicleCC, setOnevehicleCC] = useState("");
  const [lobbyCC, setLobbyCC] = useState("");
  const [driveStatus, setDriveStatus] = useState<any>(null);
  const [guideStatus, setGuideStatus] = useState<any>(null);
  const [videopatStatus, setVideopatStatus] = useState<any>(null);
  const [janusServer, setJanusServer] = useState<string | null>(null);
  const [sdkServerUserInfo, setSdkServerUserInfo] = useState<any | null>(null);
  const [noCarError, setNoCarError] = useState<boolean | null>(null);
  const [noSignalErrorMap, setNoSignalErrorMap] = useState<
    Record<string, boolean>
  >({});
  const [noSuidError, setNoSuidError] = useState(false);
  const [hasVideoStreamMap, setHasVideoStreamMap] = useState<
    Record<string, boolean>
  >({});

  const sendData = (msg: object) => {
    if (!mqttClientRef.current) {
      console.warn("MQTT client not connected");
      return;
    }

    console.log("sendData", msg);

    mqttClientRef.current.publish(
      import.meta.env.VITE_MQTT_TOPIC,
      JSON.stringify(msg),
      (err: any) => {
        if (err) {
          console.error("MQTT publish error:", err);
        } else {
          console.log("MQTT publish success");
        }
      }
    );
  };

  const visibleVehicleCode =
    vehicleStates.find((v) => v.isVisible)?.code ?? null;

  const selectedVehicle = vehicleList.find(
    (v) => v.vehicle_communication_code === visibleVehicleCode
  );
  const selectedVehicleId = selectedVehicle?.vehicle_id;

  useEffect(() => {
    return () => {
      Object.values(audioMonitorRef.current).forEach((monitor) => {
        if (monitor) monitor.stop();
      });
    };
  }, []);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const user = await authApiService.me();

        if (user.role_cd !== "01") {
          navigate("/login");
          return;
        }

        setBaseId(user.base_id);
        setMonitorConsoleId(user.monitor_console_id);
        setOnevehicleCC(user.communication_code);

        const response = await operatorApiService.doGetCommunicationCode({
          role_cd: "03",
          monitor_console_id: user.monitor_console_id,
        });

        setLobbyCC(response);
      } catch (error) {
        navigate("/login");
      }
    };

    fetchMe();
  }, [navigate]);

  useEffect(() => {
    if (monitorConsoleId === null) return;

    const fetchInitData = async () => {
      try {
        const janusConfig = await operatorApiService.doGetJanusConfig({
          baseId,
        });

        setJanusServer(janusConfig.janusServer);
        setSdkServerUserInfo({ baseId });

        const response = await operatorApiService.doGetInitDataForOnevehicle({
          monitorConsoleId,
        });
        setInitData(response);

        const vccs = response?.vehicleCommunicationCodes ?? [];
        setVehicleList(vccs);

        const selectedCode =
          response?.selectingVehicle?.vehicle_communication_code ?? null;
        setVehicleStates(
          vccs.map((v: any) => ({
            code: v.vehicle_communication_code,
            isVisible: v.vehicle_communication_code === selectedCode,
          }))
        );

        const label = response?.selectingVehicle?.vehicle_label ?? "";
        setVehicleLabel(label);
        const pat = response?.selectingVehicle?.movie_switching ?? "";
        setVideopatStatus({ videoPat: pat });
        const guide = response?.selectingVehicle?.guide_switching ?? "";
        setGuideStatus({ gi: guide });
      } catch (error) {
        console.error("初期データ取得エラー:", error);
      }
    };

    fetchInitData();
  }, [monitorConsoleId]);

  // mqtt
  useEffect(() => {
    const fetchSelectVehicle = async () => {
      try {
        const response = await operatorApiService.doGetInitDataForOnevehicle({
          monitorConsoleId,
        });
        setInitData(response);

        const vccs = response?.vehicleCommunicationCodes ?? [];
        setVehicleList((prev) => {
          const prevCodes = prev
            .map((v) => v.vehicle_communication_code)
            .sort();
          const nextCodes = vccs
            .map((v: any) => v.vehicle_communication_code)
            .sort();
          const isSame =
            prevCodes.length === nextCodes.length &&
            prevCodes.every((code, i) => code === nextCodes[i]);
          return isSame ? prev : vccs;
        });

        const pat = response?.selectingVehicle?.movie_switching ?? "";
        setVideopatStatus({ videoPat: pat });
        const guide = response?.selectingVehicle?.guide_switching ?? "";
        setGuideStatus({ gi: guide });
      } catch (error) {
        console.error("選択車両データ取得エラー:", error);
      }
    };

    const mqttRoleId = import.meta.env.VITE_MQTT_ONEVEHICLE_ROLE_ID;
    const client = initMqttClient(mqttRoleId, {
      onMessage: (json) => {
        const driveData = recvDataSetCarDrive(json);
        if (driveData) {
          if (initData?.vehicleCommunicationCode == driveData.sr) {
            setDriveStatus(driveData);
          }
        }

        // ガイド線指示は画面更新指示に統合
        const guideData = recvDataUpdateScreen(json);
        if (guideData) {
          if (onevehicleCC == guideData.dt) {
            setGuideStatus(guideData);
          }
        }

        const videopatData = recvDataSetVideoSelect(json);
        if (videopatData) {
          if (initData?.vehicleCommunicationCode == videopatData.source) {
            setVideopatStatus(videopatData);
          }
        }

        const selectCarData = recvDataSelectCarRet(json);
        if (selectCarData) {
          const consoleId = Number(selectCarData.destination.slice(-2));
          if (consoleId !== monitorConsoleId) return;

          const matchedVehicle = initData?.vehicleCommunicationCodes.find(
            (v) => {
              return (
                v.vehicle_communication_code === selectCarData.source &&
                selectCarData.destination === initData.monitorCommunicationCode
              );
            }
          );
          if (!matchedVehicle) return;

          if (selectCarData.control === 0) {
            if (visibleVehicleCode === selectCarData.source) {
              setDriveStatus(null);
              setGuideStatus(null);
              setVideopatStatus(null);
            }

            setVehicleStates((prev) =>
              prev.map((v) =>
                v.code === selectCarData.source ? { ...v, isVisible: false } : v
              )
            );
          }

          if (selectCarData.control === 1) {
            setDriveStatus(null);
            setGuideStatus(null);
            setVideopatStatus(null);
            fetchSelectVehicle();
            setVehicleStates((prev) =>
              prev.map((v) => ({
                ...v,
                isVisible: v.code === selectCarData.source,
              }))
            );
            const label = matchedVehicle.vehicle_label ?? "";
            setVehicleLabel(label);
          }
        }
      },
    });

    mqttClientRef.current = client;

    return () => {
      client.end(true);
    };
  }, [initData]);

  // janus
  useEffect(() => {
    if (
      !initData ||
      vehicleList.length === 0 ||
      !janusServer ||
      !sdkServerUserInfo
    )
      return;

    vehicleList.forEach((vehicle) => {
      const vcc = vehicle.vehicle_communication_code;
      const suid = vehicle.zao_suid;
      const videoEl = videoRefs.current[vcc];

      if (!suid || !videoEl) return;

      setNoSignalErrorMap((prev) => ({ ...prev, [vcc]: true }));

      initJanusForOneVehicle({
        janusServer,
        sdkServerUserInfo,
        suid,
        videoEl,
        onError: (err, suidArg) => {
          const vccForSuid = vehicleList.find(
            (v) => v.zao_suid === suidArg
          )?.vehicle_communication_code;
          if (err == "noSignal" && vccForSuid) {
            setNoSignalErrorMap((prev) => ({ ...prev, [vccForSuid]: true }));
          }
          console.error(`Janus接続エラー: ${vccForSuid || vcc}`, err);
        },
        onStreamStart: () => {
          setNoSignalErrorMap((prev) => ({ ...prev, [vcc]: false }));
        },
        onAudioStream: (stream: MediaStream) => {
          const vcc = vehicle.vehicle_communication_code;
          if (audioMonitorRef.current[vcc]) {
            audioMonitorRef.current[vcc]!.stop();
          }
          audioMonitorRef.current[vcc] = startAudioMonitor(
            stream,
            vehicle.vehicle_communication_code,
            onevehicleCC,
            lobbyCC,
            vehicle.audio_alive_freq,
            sendData
          );
        },
        onRemoteHandle: (handle) => {
          remoteFeedsRef.current[vcc] = handle;
          suidsRef.current[vcc] = suid;
        },
      });
    });
  }, [vehicleList]);

  useEffect(() => {
    if (vehicleList.length === 0) return;
    if (!visibleVehicleCode) {
      setNoCarError(true);
    } else {
      setNoCarError(false);

      const selected = vehicleList.find(
        (v) => v.vehicle_communication_code === visibleVehicleCode
      );
      if (!selected?.zao_suid) {
        setNoSuidError(true);
      } else {
        setNoSuidError(false);
      }
    }
  }, [visibleVehicleCode, vehicleList]);

  useEffect(() => {
    const interval = setInterval(() => {
      sendOneVehicleLog();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!initData || monitorConsoleId === null) return;

    const interval = setInterval(() => {
      startLayoutCheck(initData, monitorConsoleId);
    }, 1000);

    return () => clearInterval(interval);
  }, [initData]);

  useEffect(() => {
    if (!sdkServerUserInfo) return;

    const interval = setInterval(() => {
      setPlayoutDelay(
        remoteFeedsRef.current,
        suidsRef.current,
        sdkServerUserInfo
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [sdkServerUserInfo]);

  const visibleNoSignalError = visibleVehicleCode
    ? noSignalErrorMap[visibleVehicleCode]
    : false;

  return (
    <div className="videoWrapper">
      {visibleVehicleCode && <div className="vehicleLabel">{vehicleLabel}</div>}
      <div className="videoContainer">
        {vehicleStates.map(({ code, isVisible }) => (
          <video
            key={code}
            ref={(el) => {
              if (el) videoRefs.current[code] = el;
            }}
            autoPlay
            playsInline
            onLoadedMetadata={() => {
              const currentVideoEl = videoRefs.current[code];
              const updateHasVideo = () => {
                if (
                  currentVideoEl &&
                  currentVideoEl.srcObject instanceof MediaStream &&
                  currentVideoEl.srcObject.getVideoTracks().length > 0
                ) {
                  setHasVideoStreamMap((prev) => ({ ...prev, [code]: true }));
                } else {
                  setHasVideoStreamMap((prev) => ({ ...prev, [code]: false }));
                }
              };
              updateHasVideo();
              // trackのended/unmuteイベント監視
              if (
                currentVideoEl &&
                currentVideoEl.srcObject instanceof MediaStream
              ) {
                currentVideoEl.srcObject.getVideoTracks().forEach((track) => {
                  track.onended = () => {
                    setHasVideoStreamMap((prev) => ({
                      ...prev,
                      [code]: false,
                    }));
                  };
                  // trackがunmuteされたら映像復帰
                  track.onunmute = () => {
                    setHasVideoStreamMap((prev) => ({ ...prev, [code]: true }));
                  };
                });
              }
            }}
            onEmptied={() => {
              setHasVideoStreamMap((prev) => ({ ...prev, [code]: false }));
            }}
            onPlay={() => {
              // 再生開始時にも再判定
              const currentVideoEl = videoRefs.current[code];
              if (
                currentVideoEl &&
                currentVideoEl.srcObject instanceof MediaStream &&
                currentVideoEl.srcObject.getVideoTracks().length > 0
              ) {
                setHasVideoStreamMap((prev) => ({ ...prev, [code]: true }));
              }
            }}
            onCanPlay={() => {
              const currentVideoEl = videoRefs.current[code];
              if (
                currentVideoEl &&
                currentVideoEl.srcObject instanceof MediaStream &&
                currentVideoEl.srcObject.getVideoTracks().length > 0
              ) {
                setHasVideoStreamMap((prev) => ({ ...prev, [code]: true }));
              }
            }}
            onPlaying={() => {
              const currentVideoEl = videoRefs.current[code];
              if (
                currentVideoEl &&
                currentVideoEl.srcObject instanceof MediaStream &&
                currentVideoEl.srcObject.getVideoTracks().length > 0
              ) {
                setHasVideoStreamMap((prev) => ({ ...prev, [code]: true }));
              }
            }}
            style={
              isVisible &&
              !visibleNoSignalError &&
              !noCarError &&
              !noSuidError &&
              hasVideoStreamMap[code]
                ? { display: "block" }
                : { display: "none" }
            }
          />
        ))}
        {visibleVehicleCode &&
          !visibleNoSignalError &&
          !noCarError &&
          !noSuidError &&
          hasVideoStreamMap[visibleVehicleCode || ""] && (
            <>
              <GuideCanvas
                selectingVehicleId={selectedVehicleId}
                guide={guideStatus?.gi}
                videoPat={videopatStatus?.videoPat}
              />
              <StatusOverlay
                status={driveStatus}
                videoPat={videopatStatus?.videoPat}
              />
            </>
          )}
      </div>

      {noCarError ? (
        <div className="noVideo">
          <img src={unplayIcon} alt="No Signal" />
          <div className="noVideoText">車両が選択されていません。</div>
        </div>
      ) : visibleNoSignalError ||
        noSuidError ||
        !visibleVehicleCode ||
        !hasVideoStreamMap[visibleVehicleCode] ? (
        <div className="noVideo">
          <img src={unplayIcon} alt="No Signal" />
          <div className="noVideoText">入力信号がありません</div>
        </div>
      ) : null}
    </div>
  );
};

export default OneVehicle;
